import { t } from "i18next";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useFetch } from "../../../../hooks/useFetch";
import { ScreenType } from "../../../../types/types";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { Button } from "../../../../ui/Button";
import { useAppStore } from "../../../../stores/RootStore";
import { useOutletContext } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { EquipmentsTableData } from "../../../../types/formDataTypes";
import { DataTable } from "../../../../ui/DataTable/DataTable";
import { useScreenStore } from "../../../../stores/ScreenStore";
import { SimpleDataTable } from "../../../../ui/DataTable/SimpleDataTable";

type Props = {
  updateMonitor: Function;
};

const ScreenEquipmentTab = ({updateMonitor}:Props) => {
  const {monitor} = useScreenStore();
  const {screens, setScreens} = useAppStore();
  const equipments = useAppStore((store) => store.equipments);

  const [screenId, setScreenId] = useState("");

  const divisions = useAppStore((store) => store.divisions);
  const divisionsList = divisions?.map((division) => {
    return { value: division.id, label: division.attributes.name };
  });

  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] = useFetch(
    `/directories/screens/${monitor?.id}/equipment_items`
  );

  const [{ response: deleteResponse, isLoading: deleteIsLoading, error: deleteError }, doFetchItemDelete] = useFetch(
    `/directories/screens/${monitor?.id}/equipment_items/${screenId}`
  );

  useEffect(() => {
    if (!saveResponse) return;
    updateMonitor()
  }, [saveResponse]);

  useEffect(() => {
    if (!deleteResponse) return;
    updateMonitor()
  }, [deleteResponse]);

  let nodes = equipments?.map((equipment) => {
    return {
      id: equipment.id,
      parent_id: equipment.attributes.parent_id,
      childs: equipment.attributes.children_ids,
      inventory_number: equipment.attributes.inventory_number,
      name: equipment.attributes.name,
      equipment_model_name: equipment.attributes.equipment_model_name,
      equipment_type_name: equipment.attributes.equipment_type_name,
      producer_name: equipment.attributes.producer_name,
      factory_number: equipment.attributes.factory_number,
      division_name: equipment.attributes.division_name,
      with_sensors: String(equipment.attributes.with_sensors),
      status: equipment.attributes.status,
      parent_changed_at: equipment.attributes.parent_changed_at,
      status_changed_at: equipment.attributes.status_changed_at,
      division_changed_at: equipment.attributes.division_changed_at,
      qrcode: equipment.attributes.qrcode,
    };
  });

  nodes = _.sortBy(nodes, []);
  let data = { nodes };

  const disconnectEquipment = (screenId: string) => {
    setScreenId(screenId);
    doFetchItemDelete({
      method: "DELETE",
    });
  };

  const connectEquipment = (id: string) => {
    doFetchItemSave({
      method: "POST",
      data: {
        data: {
          equipment_item_id: id,
        },
      },
    });
  };

  const columns: ColumnDef<EquipmentsTableData>[] = [
    {
      id: "equipment_type_name",
      accessorFn: (row) => `${row.equipment_type_name}, ${row.producer_name}, ${row.equipment_model_name}`,
      header: t("equipment_name"),
      size: 1000,
      cell: (props) => (
        <div>
          <div className="font-medium">{props.row.original.name}</div>
          <div>{`${props.row.getValue("equipment_type_name")}`}</div>
        </div>
      ),
    },
    {
      header: `${t("inventory_number")}`,
      size: 150,
      maxSize: 150,
      minSize: 150,
      cell: (props) => props.row.original.inventory_number,
    },
    {
      accessorKey: "division_name",
      header: t("division"),
      size: 250,
      maxSize: 250,
      minSize: 250,
      cell: ({ row }) => row.getValue("division_name"),
    },
    {
      accessorKey: "id",
      header: "",
      size: 150,
      maxSize: 150,
      minSize: 150,
      cell: ({ row }) => (
        <div className="flex w-full items-center gap-1 justify-end">
          {monitor?.attributes.equipment_item_relations.find(
            (eq) => String(eq?.equipment_item?.id!) == row.original.id
          ) ? (
            <Button
              variant="destructive"
              size="xs"
              onClick={() => {
                disconnectEquipment(
                  String(
                    monitor?.attributes.equipment_item_relations.find(
                      (eq) => String(eq.equipment_item.id) == row.original.id
                    )?.id
                  )
                );
              }}
            >
              {t("disconnect")}
            </Button>
          ) : (
            <Button
              variant="default"
              size="xs"
              onClick={() => {
                connectEquipment(row.original.id);
              }}
            >
              {t("connect")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="relative flex flex-col w-full h-full overflow-hidden bg-background px-6">
      <div className="flex flex-none w-full h-24 text-base gap-2 items-center justify-between px-6">
        <div className="flex flex-col space-y-1">
          <span className="text-2xl font-semibold">{t('choose_equipment_for_screen')}</span>
        </div>
      </div>
      <ScrollArea className="px-2">
        <DataTable data={data.nodes} columns={columns} />
      </ScrollArea>
    </div>
  );
};

export default ScreenEquipmentTab;
